import { dateFormatDynamic } from "../../../utils/datetime";
import { DateFormats } from "../../../utils/formatters";
import { salaryCurrency } from "../../company/JobManagement/model/jobEnums";

export interface Subscription {
  id: string;
  name: string;
  price_monthly: number;
  price_yearly: number;
  billing_cycle: "monthly" | "yearly";
  features: string[];
  credits_per_dollar?: number | null;
  max_users?: number;
  max_candidates?: number;
  is_custom: boolean;
  yearly_discount_percent?: number;
  volume_discount?: {
    min_users: number;
    discount_percent: number;
  };
  active_companies: number;
  total_revenue: number;
  avg_candidates_processed: number;
  created_at: string;
  status: "active" | "inactive";
  is_active: boolean;
  monthly_subscribers: number;
  yearly_subscribers: number;
}

export interface SubscriptionSummary {
  total_plans: number;
  active_plans: number;
  total_companies: number;
  total_revenue: number;
  average_revenue_per_plan: number;
  monthly_recurring_revenue: number;
}

export interface SubscriptionPlansResponse {
  plans: Subscription[];
  summary: SubscriptionSummary;
}

export interface CreatePlanFormData {
  tier_type: "STARTER" | "PRO" | "ENTERPRISE";
  name: string;
  slug: string;
  description: string;
  features: string;
  sort_order: number;
  is_public: boolean;
  badge_text: string;
  price_monthly: string;
  price_yearly: string;
  currency: string;
  credits_per_dollar: string;
  yearly_bonus_percent: string;
  monthly_bonus_percent: string;
  topup_discount_percent: string;
  effective_from: string;
  stripe_price_id_monthly: string;
  stripe_price_id_yearly: string;
  stripe_product_id: string;
  notes: string;
}

export interface EditPlanFormData {
  name: string;
  slug: string;
  description: string;
  is_active: boolean;
  is_public: boolean;
  sort_order: number;
  badge_text: string;
  features: string;
}

export interface EditVersionFormData {
  price_monthly: string;
  price_yearly: string;
  currency: string;
  credits_per_dollar: string;
  topup_discount_percent: string;
  yearly_bonus_percent: string;
  monthly_bonus_percent: string;
  effective_from: string;
  effective_until: string;
  status: "DRAFT" | "SCHEDULED" | "ACTIVE" | "EXPIRED";
  stripe_price_id_monthly: string;
  stripe_price_id_yearly: string;
  stripe_product_id: string;
  notes: string;
}

export interface TierPricingDetails {
  id?: string | null;
  tier_id?: string | null;
  price_monthly?: string | number | null;
  price_yearly?: string | number | null;
  currency?: string | null;
  credits_per_dollar?: number | null;
  topup_discount_percent?: string | number | null;
  yearly_bonus_percent?: string | number | null;
  monthly_bonus_percent?: string | number | null;
  version_number?: number | null;
  effective_from?: string | null;
  effective_until?: string | null;
  status?: string | null;
  stripe_price_id_monthly?: string | null;
  stripe_price_id_yearly?: string | null;
  stripe_product_id?: string | null;
  notes?: string | null;
  created_at?: string | null;
}

export interface TierDetails {
  id: string;
  tier_type?: string | null;
  slug?: string | null;
  name?: string | null;
  description?: string | null;
  is_active?: boolean | null;
  is_public?: boolean | null;
  sort_order?: number | null;
  badge_text?: string | null;
  features?: string[] | string | null;
  created_at?: string | null;
  current_pricing?: TierPricingDetails | null;
}

export interface ToastState {
  message: string;
  type: "success" | "error" | "warning" | "info";
  isVisible: boolean;
}

export type SubscriptionTab = "plans" | "credit-actions";

export interface SubscriptionTierOption {
  id: string;
  name: string;
}

export interface BillingCurrencyOption {
  label: string;
  value: string;
}

export interface UpdateBillingTierPayload {
  name: string;
  slug: string;
  description?: string;
  is_active: boolean;
  is_public: boolean;
  sort_order: number;
  badge_text?: string;
  features: string[];
}

export interface CreateBillingTierVersionPayload {
  price_monthly: number;
  price_yearly: number;
  currency: string;
  credits_per_dollar: number;
  topup_discount_percent: number;
  yearly_bonus_percent: number;
  monthly_bonus_percent: number;
  effective_from: string;
  stripe_price_id_monthly: string;
  stripe_price_id_yearly: string;
  stripe_product_id: string;
  notes?: string | null;
}

export interface CreateBillingTierPayload {
  tier_type: CreatePlanFormData["tier_type"];
  name: string;
  slug: string;
  description?: string;
  sort_order: number;
  is_public: boolean;
  badge_text?: string;
  features: string[];
  is_active: true;
  initial_pricing: CreateBillingTierVersionPayload;
}

export const initialCreatePlanForm: CreatePlanFormData = {
  tier_type: "STARTER",
  name: "",
  slug: "",
  description: "",
  features: "",
  sort_order: 1,
  is_public: true,
  badge_text: "",
  price_monthly: "",
  price_yearly: "",
  currency: "USD",
  credits_per_dollar: "",
  yearly_bonus_percent: "",
  monthly_bonus_percent: "",
  topup_discount_percent: "",
  effective_from: "",
  stripe_price_id_monthly: "",
  stripe_price_id_yearly: "",
  stripe_product_id: "",
  notes: "",
};

export const initialEditPlanForm: EditPlanFormData = {
  name: "",
  slug: "",
  description: "",
  is_active: true,
  is_public: true,
  sort_order: 1,
  badge_text: "",
  features: "",
};

export const initialEditVersionForm: EditVersionFormData = {
  price_monthly: "",
  price_yearly: "",
  currency: "USD",
  credits_per_dollar: "",
  topup_discount_percent: "",
  yearly_bonus_percent: "",
  monthly_bonus_percent: "",
  effective_from: "",
  effective_until: "",
  status: "SCHEDULED",
  stripe_price_id_monthly: "",
  stripe_price_id_yearly: "",
  stripe_product_id: "",
  notes: "",
};

export const billingCurrencyOptions: BillingCurrencyOption[] = Object.values(
  salaryCurrency,
).map((currency) => ({
  label: currency,
  value: currency,
}));

type StripeIdentifierFormData = Pick<
  CreatePlanFormData,
  "stripe_price_id_monthly" | "stripe_price_id_yearly" | "stripe_product_id"
>;

const getBillingCurrency = (currency: string) => {
  const normalizedCurrency = currency.trim().toUpperCase();
  const isSupportedCurrency = billingCurrencyOptions.some(
    (option) => option.value === normalizedCurrency,
  );

  if (!isSupportedCurrency) {
    throw new Error("Choose a valid currency.");
  }

  return normalizedCurrency;
};

const getRequiredStripeIdentifiers = (form: StripeIdentifierFormData) => {
  const stripePriceIdMonthly = form.stripe_price_id_monthly.trim();
  const stripePriceIdYearly = form.stripe_price_id_yearly.trim();
  const stripeProductId = form.stripe_product_id.trim();

  if (!stripePriceIdMonthly || !stripePriceIdYearly || !stripeProductId) {
    throw new Error(
      "Stripe monthly price ID, yearly price ID, and product ID are required.",
    );
  }

  return {
    stripe_price_id_monthly: stripePriceIdMonthly,
    stripe_price_id_yearly: stripePriceIdYearly,
    stripe_product_id: stripeProductId,
  };
};

export const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

export const mapTierToEditForm = (tier: TierDetails): EditPlanFormData => ({
  name: tier.name || "",
  slug: tier.slug || "",
  description: tier.description || "",
  is_active: tier.is_active ?? true,
  is_public: tier.is_public ?? true,
  sort_order: tier.sort_order || 1,
  badge_text: tier.badge_text || "",
  features: Array.isArray(tier.features)
    ? tier.features.join("\n")
    : tier.features || "",
});

export const normalizeTierVersions = (
  versions: TierPricingDetails[] | { versions?: TierPricingDetails[] },
) => (Array.isArray(versions) ? versions : versions?.versions || []);

export const isScheduledVersion = (version: TierPricingDetails) =>
  version.status?.toUpperCase() === "SCHEDULED";

const parseFeatures = (features: string) =>
  features
    .split("\n")
    .map((feature) => feature.trim())
    .filter(Boolean);

const dateInputToStartOfDay = (value: string) => {
  if (!value) {
    return undefined;
  }

  const dateParts = value.split("-");
  if (dateParts.length !== 3) {
    return undefined;
  }

  return value ? dateFormatDynamic(value, DateFormats.dateTimeAt) : undefined;
};

export const buildUpdatePlanPayload = (
  form: EditPlanFormData,
): UpdateBillingTierPayload => ({
  name: form.name.trim(),
  slug: form.slug.trim(),
  description: form.description.trim() || undefined,
  is_active: form.is_active,
  is_public: form.is_public,
  sort_order: form.sort_order,
  badge_text: form.badge_text.trim() || undefined,
  features: parseFeatures(form.features),
});

export const buildCreatePlanPayload = (
  form: CreatePlanFormData,
): CreateBillingTierPayload => {
  const monthlyPrice = Number(form.price_monthly);
  const yearlyPrice = Number(form.price_yearly);
  const creditsPerDollar = Number(form.credits_per_dollar);
  const effectiveFrom = dateInputToStartOfDay(form.effective_from);

  if (!Number.isFinite(monthlyPrice) || !Number.isFinite(yearlyPrice)) {
    throw new Error("Enter valid monthly and yearly prices.");
  }

  if (!Number.isInteger(creditsPerDollar) || creditsPerDollar < 1) {
    throw new Error("Credits per dollar must be at least 1.");
  }

  if (!effectiveFrom) {
    throw new Error("Choose a valid effective from date.");
  }

  const stripeIdentifiers = getRequiredStripeIdentifiers(form);
  const currency = getBillingCurrency(form.currency);

  return {
    tier_type: form.tier_type,
    name: form.name.trim(),
    slug: form.slug.trim(),
    description: form.description.trim() || undefined,
    sort_order: form.sort_order,
    is_public: form.is_public,
    badge_text: form.badge_text.trim() || undefined,
    features: parseFeatures(form.features),
    is_active: true,
    initial_pricing: {
      price_monthly: monthlyPrice,
      price_yearly: yearlyPrice,
      currency,
      credits_per_dollar: creditsPerDollar,
      yearly_bonus_percent: Number(form.yearly_bonus_percent || 0),
      monthly_bonus_percent: Number(form.monthly_bonus_percent || 0),
      topup_discount_percent: Number(form.topup_discount_percent || 0),
      effective_from: effectiveFrom,
      ...stripeIdentifiers,
      notes: form.notes.trim() || undefined,
    },
  };
};

export const buildCreateVersionPayload = (
  form: EditVersionFormData,
): CreateBillingTierVersionPayload => {
  const monthlyPrice = Number(form.price_monthly);
  const yearlyPrice = Number(form.price_yearly);
  const creditsPerDollar = Number(form.credits_per_dollar);

  if (!Number.isFinite(monthlyPrice) || !Number.isFinite(yearlyPrice)) {
    throw new Error("Enter valid monthly and yearly prices.");
  }

  if (!Number.isInteger(creditsPerDollar) || creditsPerDollar < 1) {
    throw new Error("Credits per dollar must be at least 1.");
  }

  const effectiveFrom = dateInputToStartOfDay(form.effective_from);
  if (!effectiveFrom) {
    throw new Error("Choose a valid effective from date.");
  }

  const stripeIdentifiers = getRequiredStripeIdentifiers(form);
  const currency = getBillingCurrency(form.currency);

  return {
    price_monthly: monthlyPrice,
    price_yearly: yearlyPrice,
    currency,
    credits_per_dollar: creditsPerDollar,
    topup_discount_percent: Number(form.topup_discount_percent || 0),
    yearly_bonus_percent: Number(form.yearly_bonus_percent || 0),
    monthly_bonus_percent: Number(form.monthly_bonus_percent || 0),
    effective_from: effectiveFrom,
    ...stripeIdentifiers,
    notes: form.notes.trim() || null,
  };
};
